import styled, { css, keyframes } from "styled-components";

/*
 * Bendovi listing, v2 redizajn (s.4.1): kompaktne cover kartice,
 * stat traka u jednoj liniji, žanr chipovi u URL-u.
 *
 * Animacije: intro za naslov/statistiku/filtere na mount, kartice se
 * otkrivaju pojedinačno na scroll (useReveal per kartica, $visible + --i).
 */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to { opacity: 1; transform: translateY(0); }
`;

const labelIn = keyframes`
  from { opacity: 0; letter-spacing: 0.45em; }
  to { opacity: 1; letter-spacing: 0.15em; }
`;

export const Page = styled.div`
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 5rem;
`;

export const SectionLabel = styled.div`
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 0.6rem;
  animation: ${labelIn} 0.9s cubic-bezier(0.2, 0.8, 0.2, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const PageH1 = styled.h1`
  margin: 0 0 1.25rem;
  font-family: var(--font-display);
  font-size: clamp(40px, 6vw, 72px);
  line-height: 0.95;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--text-primary);
  animation: ${fadeUp} 0.65s cubic-bezier(0.2, 0.8, 0.2, 1) 0.08s both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* Stat traka, jedna linija, ne kutije */
export const StatStrip = styled.div`
  display: flex;
  align-items: baseline;
  gap: 2rem;
  flex-wrap: wrap;
  padding: 1rem 0;
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  margin-bottom: 1.75rem;

  /* statovi ulaze jedan za drugim kad podaci stignu */
  & > * {
    animation: ${fadeUp} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
  }

  ${Array.from(
    { length: 4 },
    (_, i) => css`
      & > *:nth-child(${i + 1}) {
        animation-delay: ${80 + i * 90}ms;
      }
    `,
  )}

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }
`;

export const Stat = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;

  strong {
    font-family: var(--font-display);
    font-size: 1.5rem;
    color: var(--brand);
    line-height: 1;
  }

  span {
    color: var(--text-secondary);
    font-size: 0.85rem;
  }
`;

/* Tabovi + žanr filteri */
export const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  flex-wrap: wrap;
  margin-bottom: 1.75rem;
  animation: ${fadeUp} 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) 0.2s both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const TabChips = styled.div`
  display: flex;
  gap: 0.4rem;
  padding-right: 0.9rem;
  border-right: 1px solid var(--border-strong);

  @media (max-width: 560px) {
    border-right: none;
    padding-right: 0;
  }
`;

export const ChipsRail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  overflow-x: auto;
  scrollbar-width: none;
  flex: 1;
  min-width: 0;

  &::-webkit-scrollbar {
    display: none;
  }

  /* žanr chipovi ulete jedan za drugim kad se učitaju */
  & > * {
    animation: ${fadeUp} 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
  }

  ${Array.from(
    { length: 14 },
    (_, i) => css`
      & > *:nth-child(${i + 1}) {
        animation-delay: ${i * 40}ms;
      }
    `,
  )}

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }
`;

export const FilterChip = styled.button`
  flex-shrink: 0;
  height: 34px;
  padding: 0 0.9rem;
  border-radius: var(--radius-pill);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s ease;
  background: ${(p) => (p.$active ? "var(--brand-dim)" : "transparent")};
  color: ${(p) => (p.$active ? "var(--brand)" : "var(--text-secondary)")};
  border: 1px solid
    ${(p) => (p.$active ? "rgba(255, 107, 53, 0.45)" : "var(--border-strong)")};

  &:hover {
    color: ${(p) => (p.$active ? "var(--brand)" : "var(--text-primary)")};
  }
`;

/* ------------------------------- kartice ------------------------------- */
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const BandCard = styled.article`
  position: relative;
  display: block;
  /* renderuje se kao <Link> (as prop), poništi default stilove ankera */
  text-decoration: none;
  color: inherit;
  aspect-ratio: 16 / 9;
  border-radius: var(--radius-card);
  border: 1px solid var(--border);
  overflow: hidden;
  cursor: pointer;
  background: var(--bg-elevated);
  transition:
    border-color 0.2s ease,
    transform 0.2s ease;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  /* gradijent samo pri dnu, bez narandžastog tinta */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      transparent 40%,
      rgba(19, 16, 16, 0.85) 100%
    );
  }

  &:hover {
    border-color: var(--border-strong);
    transform: translateY(-2px);

    img {
      transform: scale(1.04);
    }
  }

  /* reveal na scroll, svaka kartica kad uđe u viewport, kaskadno po
     koloni u redu (--i = indeks kolone) */
  opacity: 0;

  ${(p) =>
    p.$visible &&
    css`
      opacity: 1;
      animation: ${fadeUp} 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
      animation-delay: calc(var(--i, 0) * 70ms);
    `}

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`;

export const CardOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

export const BandName = styled.h3`
  margin: 0;
  align-self: flex-start;
  position: relative;
  font-family: var(--font-display);
  font-size: 22px;
  line-height: 1;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--text-primary);

  /* brand underline se prevuče ispod imena na hover kartice */
  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: -4px;
    height: 2px;
    background: var(--brand);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.25s ease;
  }

  ${BandCard}:hover &::after {
    transform: scaleX(1);
  }
`;

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

export const MetaLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  color: var(--text-secondary);
  font-size: 13px;

  .dot {
    color: var(--text-muted);
  }
`;

export const GenreChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 0.6rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-pill);
  color: var(--text-secondary);
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
`;

export const ProductsLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: var(--brand);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;

  svg {
    transition: transform 0.18s ease;
  }

  ${BandCard}:hover & svg {
    transform: translateX(3px);
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 5rem 1rem;
  color: var(--text-secondary);
  animation: ${fadeUp} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) both;

  svg {
    color: var(--text-muted);
    margin-bottom: 1rem;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const LoadMoreRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2.5rem;
  animation: ${fadeUp} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) 0.25s backwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 44px;
  padding: 0 1.5rem;
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    border-color: var(--brand);
    color: var(--brand);
  }
`;
